import { useMemo } from 'react';
import type { FolderLinkedBriefDto } from '@inference.engine/workspace-api-client/web';
import { useQuery } from '@tanstack/react-query';

import { briefsQueryOptions } from '@/entities/brief';

/** One request covers a project's briefs; the endpoint caps a page here. */
const BRIEF_PAGE_SIZE = 100;

export interface BriefOption {
  id: string;
  title: string;
}

interface UseReadyBriefOptionsParams {
  projectId: string;
  /**
   * The brief already linked to the folder. It has to be re-offered
   * explicitly or the list would fail to mark the very brief the surface is
   * naming: a brief leaves Ready the moment its status moves on, and it can
   * also sit beyond the first page.
   */
  currentBrief?: FolderLinkedBriefDto | null;
  /** A surface that only reveals the list on demand defers the request. */
  enabled?: boolean;
}

/**
 * The briefs a folder can be pointed at, in the order they are offered.
 *
 * Every ready brief is offerable: a brief is delivered into as many folders as
 * the work needs, so one already attached elsewhere is not spoken for.
 */
export function useReadyBriefOptions(params: UseReadyBriefOptionsParams) {
  const { projectId, currentBrief, enabled = true } = params;

  const queryOptions = briefsQueryOptions({
    projectId,
    page: 1,
    pageSize: BRIEF_PAGE_SIZE,
    status: ['submitted'],
  });

  const { data, isLoading, isError } = useQuery({
    ...queryOptions,
    // Narrowing, never widening: the options already refuse a request without
    // a project, and deferring must not hand that guard back.
    enabled: enabled && queryOptions.enabled !== false,
  });

  const options = useMemo<BriefOption[]>(() => {
    const ready = (data?.items ?? []).map((brief) => ({ id: brief.id, title: brief.title }));

    const alreadyListed = currentBrief && ready.some((brief) => brief.id === currentBrief.id);
    return currentBrief && !alreadyListed
      ? [{ id: currentBrief.id, title: currentBrief.title }, ...ready]
      : ready;
  }, [data?.items, currentBrief]);

  return { options, isLoading, isError };
}
